// src/context/UserContext.jsx
import {
  createContext, useState, useEffect, useContext, useCallback, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import api, { setSessionExpiredHandler } from '../utils/api';

// Create the Context with default values.
const UserContext = createContext({
  user: null,
  loading: true,
  error: null,
  fetchUserProfile: () => { },
  updateUserContext: () => { },
  setAuthError: () => { },
  clearAuthData: () => { },
  userId: null,
  token: null,
});

// Hook to consume UserContext.
// eslint-disable-next-line react-refresh/only-export-components
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Provides user auth and profile data.
export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Clears auth data.
  const clearAuthData = useCallback(() => {
    localStorage.removeItem('userId');
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    setUser(null);
    setError(null);
    setLoading(false);
  }, []);

  // Fetches user profile.
  const fetchUserProfile = useCallback(async () => {
    setLoading(true);
    setError(null);

    const userIdToFetch = localStorage.getItem('userId');
    const tokenToFetch = localStorage.getItem('token');

    if (!userIdToFetch || !tokenToFetch) {
      setError('Please log in to view your profile.');
      setLoading(false);
      setUser(null);
      return;
    }

    try {
      const res = await api.get('/users/me');
      setUser(res.data);
    } catch (err) {
      setError(`Failed to load user profile: ${err.message}`);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch profile on mount.
  useEffect(() => {
    const initialUserId = localStorage.getItem('userId');
    const initialToken = localStorage.getItem('token');

    if (initialUserId && initialToken) {
      fetchUserProfile();
    } else {
      setLoading(false);
      setUser(null);
      setError('Please log in to view your profile.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Session expired handler
  useEffect(() => {
    setSessionExpiredHandler(() => {
      clearAuthData();
      setSessionExpired(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Updates user context and optionally fetches profile.
  const rehydrateUserContext = useCallback((userData, triggerProfileFetch = true) => {
    setUser(userData);
    if (userData?.name) {
      localStorage.setItem('userName', userData.name);
    }
    if (triggerProfileFetch) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [fetchUserProfile]);

  const updateUserContext = useCallback((updatedUserData) => {
    setUser((prevUser) => {
      if (!prevUser) return updatedUserData;
      return { ...prevUser, ...updatedUserData };
    });
    if (updatedUserData.name) {
      localStorage.setItem('userName', updatedUserData.name);
    }
  }, []);

  // Sets auth error.
  const setAuthError = useCallback((message) => {
    setError(message);
  }, []);

  // Memoized context value.
  const contextValue = useMemo(() => {
    const userIdInContext = localStorage.getItem('userId');
    const tokenInContext = localStorage.getItem('token');

    return {
      user,
      loading,
      error,
      userId: user?._id || userIdInContext,
      token: tokenInContext,
      fetchUserProfile,
      updateUserContext,
      rehydrateUserContext, // Expose new util for fast context update
      setAuthError,
      clearAuthData,
    };
  }, [user, loading, error, fetchUserProfile, updateUserContext, rehydrateUserContext, setAuthError, clearAuthData]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
      {sessionExpired && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded shadow-lg w-full max-w-sm text-center">
            <h2 className="text-lg font-semibold mb-2 text-red-600">Session Expired</h2>
            <p className="mb-4">Your session has expired. Please log in again.</p>
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={() => {
                setSessionExpired(false);
                window.location.href = '/login';
              }}
            >
              Log In
            </button>
          </div>
        </div>
      )}
    </UserContext.Provider>
  );
}

// PropTypes validation for UserProvider
UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
